import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/api/types/agent-executions";
import { UUID } from "crypto";

export default class APIAgentExecutions extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * List agent executions with pagination
   */
  getAgentExecutions = async ({
    agent_type,
    status,
    search,
    limit = 50,
    offset = 0,
  }: {
    agent_type?: string;
    status?: number;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<types.PaginatedAgentExecutionsResponse> => {
    const params = new URLSearchParams();
    if (agent_type) params.append("agent_type", agent_type);
    if (status !== undefined) params.append("status", status.toString());
    if (search) params.append("search", search);
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    let res = await this._fetch({
      url: `/agent-executions/?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Alias for consistency
  get = async (params?: {
    agent_type?: string;
    status?: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<types.PaginatedAgentExecutionsResponse> => {
    return this.getAgentExecutions(params);
  };

  /**
   * Get single agent execution by ID
   */
  getAgentExecutionById = async (
    executionId: UUID,
  ): Promise<types.AgentExecutionResponse> => {
    let res = await this._fetch({
      url: `/agent-executions/${executionId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Cancel a running agent execution
   */
  cancelAgentExecution = async (
    executionId: UUID,
  ): Promise<types.AgentExecutionResponse> => {
    let res = await this._fetch({
      url: `/agent-executions/${executionId}/cancel`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Delete a finished or cancelled agent execution
   */
  deleteAgentExecution = async (
    executionId: UUID,
  ): Promise<{ message: string }> => {
    let res = await this._fetch({
      url: `/agent-executions/${executionId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Retry a failed or cancelled agent execution
   */
  retryAgentExecution = async (
    executionId: UUID,
  ): Promise<types.AgentExecutionResponse> => {
    let res = await this._fetch({
      url: `/agent-executions/${executionId}/retry`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
